import type { SignInReason } from '@platform/app-kit';

/** The wait page of a sign-up (`/signup/complete`). */
export const SIGNUP_COMPLETE_PATH = '/signup/complete';

/**
 * Whether `GET /me` runs after the session was (re)built. On the wait page the person's API
 * access is still being set up behind the payment (the gateway refuses every call for a minute
 * or so), so the page asks once itself, patiently, after the refresh (`waitUntilReadable`) and
 * the hook stays out of the way (#17).
 */
export function shouldReadMe(reason: SignInReason, pathname: string): boolean {
  return !((reason === 'restore' || reason === 'refresh') && pathname === SIGNUP_COMPLETE_PATH);
}

export interface WaitOptions {
  /** How many times to ask before giving up and going on (default 60). */
  attempts?: number;
  /** Between asks (default 3 s). */
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
  /** Stop asking (the page was left). */
  cancelled?: () => boolean;
}

/**
 * Ask until the API answers for the new tenant. A refusal means "not yet": the gateway lets a
 * new tenant in a little after the plan is active. Returns whether it answered; after the last
 * attempt the caller goes on anyway, and the pages cope with a refusal in plain words.
 */
export async function waitUntilReadable(
  read: () => Promise<unknown>,
  options: WaitOptions = {},
): Promise<boolean> {
  const attempts = options.attempts ?? 60;
  const delayMs = options.delayMs ?? 3_000;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (options.cancelled?.()) return false;
    try {
      await read();
      return true;
    } catch {
      await sleep(delayMs);
    }
  }
  return false;
}
